// src/pages/FAQ.jsx
import React, { useEffect } from "react";
import { ChevronDown } from "lucide-react";

const faqs = [
  {
    id: "q1",
    q: "캐릭터 정보나 전투 분석기가 제대로 인식되지 않습니다.",
    a: (
      <div className="space-y-1">
        <p>반드시 <b>1920×1080 창 모드</b>에서 사용해 주세요.</p>
        <p>다른 해상도에서는 정확도가 떨어질 수 있습니다.</p>
        <p>숫자가 잘못 나올 경우, <b>게임 내 UI 크기를 100%</b>로 맞추고 다시 시도하세요.</p>
      <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 dark:border-zinc-700 dark:bg-zinc-800">
        <p>
          <b>NVIDIA</b> 그래픽카드: GeForce Experience 또는 NVIDIA APP에서 필터/앤티앨리어싱 옵션을 기본값으로 복원한 뒤,
          NVIDIA 제어판의 [디스플레이 &gt; 바탕 화면 컬러 설정 조정] 메뉴도 기본값으로 복원하세요.
        </p>
        <p className="mt-2">
          <b>AMD</b> 그래픽카드: AMD Software: Adrenalin의 디스플레이 관련 옵션을 기본값으로 복원하세요.
        </p>
      </div>
      </div>
    ),
  },
  {
    id: "q2",
    q: "Google 계정 연결 시 어떤 정보가 수집되나요?",
    a: (
      <div>
        사용자를 구별하기 위해 <b>Google ID/Google Email</b>만 수집합니다.
      </div>
    ),
  },
  {
    id: "q3",
    q: "클라우드에 업로드한 데이터는 어떻게 사용되나요?",
    a: (
      <div>
        업로드된 데이터는 <b>통계 목적으로만 활용됩니다.</b>
      </div>
    ),
  },
  {
    id: "q4",
    q: "일부 기능이 정상적으로 작동하지 않거나, 보여야 할 버튼이 보이지 않습니다.",
    a: (
      <div>
        사용 중인 웹 브라우저에서 Ctrl + Shift + R을 눌러 <b>하드 리프레시</b>(강력한 새로고침)를 실행해 보세요.
      </div>
    ),
  },
  {
    id: "q5",
    q: "공격대 전투 분석에서 공유하기 버튼이 비활성화되거나, “공대장이 관문 설정 후 저장 시 화면 공유를 시작할 수 있어요.”라는 메시지가 나옵니다.",
    a: (
      <div className="space-y-1">
        <p>공격대 전투 분석에서 공유하기 기능은 반드시 <b>공대장이 관문을 설정</b>한 뒤에만 활성화됩니다.</p>
        <p>본인이 공대장이 아닌 경우 관문을 설정할 수 없습니다.</p>
        <p>공대장이라면, 공격대 화면 좌측 상단의 관문 선택 메뉴에서 반드시 값을 선택한 뒤 저장하세요.</p>
      </div>
    ),
  },
  {
    id: "q6",
    q: "공유하기를 누른 뒤 화면이 ‘화면 공유 시작’ 상태로 돌아갑니다.",
    a: (
      <div>
        공격대 전투 분석에서는 공유 완료 후 자동으로 초기 상태로 복귀하도록 설계되어 있습니다. 다시 화면 공유를 시작해 새로운 기록을 진행할 수 있습니다.
      </div>
    ),
  },
  {
    id: "q7",
    q: "'이미 클라우드에 저장된 항목입니다'는 메시지가 뜹니다.",
    a: (
      <div>
        같은 레이드 이름, 시간, 전투 기록이 이미 저장된 경우 중복 저장을 방지하기 위해 막습니다. 메모만 따로 기록하고 싶다면 기존 저장 기록을 삭제한 뒤 다시 저장하세요.
      </div>
    ),
  },
  {
    id: "q8",
    q: "내 데이터는 다른 사람이 볼 수 있나요?",
    a: (
      <div className="space-y-1">
        <p>클라우드에 저장된 데이터는 <b>오직 본인만 직접 접근</b>할 수 있습니다.</p>
        <p>
          다만, 서비스의 <b>통계 및 지표 산출(예: 평균 계산)</b> 목적을 위해 <b>익명화된 형태</b>로만 활용되며, 이 과정에서도 개별 기록은 외부에 공개되지 않습니다.
        </p>
        <p>즉, 본인의 기록은 본인만 확인할 수 있고, 다른 사용자는 절대 볼 수 없습니다.</p>
      </div>
    ),
  },
];

function Item({ id, q, a, openByDefault }) {
  const [open, setOpen] = React.useState(openByDefault || false);

  // 해시가 바뀔 때 해당 id면 자동 오픈
  useEffect(() => {
    const onHashChange = () => {
      if (window.location.hash === `#${id}`) {
        setOpen(true);
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    };
    window.addEventListener("hashchange", onHashChange);
    // mount 시에도 체크
    onHashChange();
    return () => window.removeEventListener("hashchange", onHashChange);
  }, [id]);

  return (
    <div
      id={id}
      className="rounded-2xl border border-zinc-200 bg-white p-4 shadow-sm transition dark:border-zinc-700 dark:bg-zinc-900"
    >
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <span className="text-base font-semibold text-zinc-900 dark:text-zinc-100">{q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`grid transition-all duration-200 ease-out ${
          open ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden text-sm leading-6 text-zinc-700 dark:text-zinc-300">
          <div className="pr-1">{a}</div>
        </div>
      </div>
    </div>
  );
}

export default function FAQ() {
  return (
    <div className="mx-auto max-w-6xl sm:px-4 lg:px-0">
      <h1 className="text-lg/8 font-semibold text-zinc-950 sm:text-xl/8 dark:text-white mb-3 lg:mb-6">FAQ</h1>
      <div className="space-y-3">
        {faqs.map((f, i) => (
          <Item key={f.id} id={f.id} q={`Q${i + 1}. ${f.q}`} a={f.a} openByDefault={i === 0} />
        ))}
      </div>
      <p className="text-right mt-8 text-xs text-zinc-500 dark:text-zinc-400">
        마지막 업데이트: {new Date().toLocaleDateString()}
      </p>
    </div>
  );
}
